// src/pages/EditPendentSpecialLoad.js
import React, { useState, useEffect, useRef, useMemo, startTransition } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "../axiosConfig";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import "../styles/EditPendentSpecialLoad.css";
import { customSelectStyles } from "../styles/reactSelectStyles";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const MAX_POINTS = 7;

// === Dimensão: limites e validador (normal vs special) ===
// Normal maxima
const NORMAL_MAX = { length: 13.6, height: 2.7, width: 2.5 };

// Special maxima (allowed if specialLoad=true)
const SPECIAL_MAX = { length: 20, height: 5, width: 4 };

// Thresholds that make a dimension "special"
const SPECIAL_THRESHOLD = { length: 13.61, height: 2.71, width: 2.51 };

const getDimLimits = (isSpecial, t) => {
  if (isSpecial) {
    return {
      length: {
        min: 0.01,
        max: SPECIAL_MAX.length,
        msg: t("validation.specialMax.length", {
          defaultValue: `❌ Special Load length cannot exceed ${SPECIAL_MAX.length.toFixed(2)}m.`,
        }),
      },
      height: {
        min: 0.01,
        max: SPECIAL_MAX.height,
        msg: t("validation.specialMax.height", {
          defaultValue: `❌ Special Load height cannot exceed ${SPECIAL_MAX.height.toFixed(2)}m.`,
        }),
      },
      width: {
        min: 0.01,
        max: SPECIAL_MAX.width,
        msg: t("validation.specialMax.width", {
          defaultValue: `❌ Special Load width cannot exceed ${SPECIAL_MAX.width.toFixed(2)}m.`,
        }),
      },
    };
  }

  return {
    length: {
      min: 0.01,
      max: NORMAL_MAX.length,
      msg: t("validation.maxLength", { defaultValue: `❌ Max length is ${NORMAL_MAX.length}m.` }),
    },
    height: {
      min: 0.01,
      max: NORMAL_MAX.height,
      msg: t("validation.maxHeight", { defaultValue: `❌ Max height is ${NORMAL_MAX.height}m.` }),
    },
    width: {
      min: 0.01,
      max: NORMAL_MAX.width,
      msg: t("validation.maxWidth", { defaultValue: `❌ Max width is ${NORMAL_MAX.width}m.` }),
    },
  };
};

const validateDim = (key, value, isSpecial, t) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "";
  }

  const limits = getDimLimits(isSpecial, t)[key];
  const num = Number(value);

  if (!Number.isFinite(num) || num <= 0) {
    return t("validation.fieldRequired", {
      defaultValue: "❌ Introduz um número válido superior a 0.",
    });
  }

  if (num > limits.max) return limits.msg;
  return "";
};

/* ---------- Caches simples por processo ---------- */
const listCache = {
  places: null,
  industries: null,
  carriers: null,
};

const makeEmptyPoint = () => ({
  location: "",
  date: "",
  hour: "",

  // 🆕 point-level dimensions
  length: "",
  width: "",
  height: "",
  lengthError: "",
  widthError: "",
  heightError: "",
  lengthErrorFlag: false,
  widthErrorFlag: false,
  heightErrorFlag: false,

  placeId: null,
  address: "",
  postalCode: "",
  city: "",
  country: "",
  gMapsLink: "",
  coordinates: "",
  contactPhone: "",

  // 🆕 Loading access
  loadingAccess: null,

  // 🆕 Handling equipment
  handlingEquipment: null,
  handlingEquipmentOther: "",

  // 🆕 Reference (optional)
  reference: "",

  showMoreFields: false,
});

function EditPendentSpecialLoad() {
  const { t } = useTranslation("edit_pendent_special_load");

  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;

  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads?view=pendent");
  };

  /* ---------- Flags ---------- */
  const [isHydrating, setIsHydrating] = useState(false);
  const [showSyncBadge, setShowSyncBadge] = useState(false);
  const [ready, setReady] = useState(() => !!prefill);

  /* ---------- Core state (renderiza já) ---------- */
  const [length, setLength] = useState(prefill?.length ?? "");
  const [height, setHeight] = useState(prefill?.height ?? "");
  const [width, setWidth] = useState(prefill?.width ?? "");
  const [weight, setWeight] = useState(prefill?.weight ?? "");

  const [merchandise, setMerchandise] = useState(prefill?.merchandise || "");
  const [merchandiseError, setMerchandiseError] = useState("");

  const [price, setPrice] = useState(prefill?.price != null ? String(prefill.price) : "");
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (only when external carrier selected)
  const [price4subcontracted, setPrice4subcontracted] = useState(
    prefill?.price4subcontracted != null ? String(prefill.price4subcontracted) : ""
  );
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  const [notes4driver, setNotes4driver] = useState(prefill?.notes4driver || "");

  const [specialLoad, setSpecialLoad] = useState(Boolean(prefill?.specialLoad));
  const dimLimits = useMemo(() => getDimLimits(specialLoad, t), [specialLoad, t]);

  // 🆕 Trailer Type
  const [trailerType, setTrailerType] = useState(prefill?.trailerType ?? "");
  const [trailerTypeOther, setTrailerTypeOther] = useState(prefill?.trailerTypeOther ?? "");
  const [trailerTypeError, setTrailerTypeError] = useState(false);
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState(false);

  // ✅ só validar "Other" depois de tentar guardar
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);

  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [weightError, setWeightError] = useState("");

  const [clientType, setClientType] = useState(
    prefill?.industryClientId ? "industry" : prefill?.carrierClientId ? "carrier" : ""
  );

  const [selectedClient, setSelectedClient] = useState(
    prefill?.industryClientId
      ? {
          value: prefill.industryClientId,
          label:
            prefill.industryClientName ||
            prefill.industryClient?.companyName ||
            t("general.industryFallback"),
        }
      : prefill?.carrierClientId
      ? {
          value: prefill.carrierClientId,
          label:
            prefill.carrierClientName ||
            prefill.carrierClient?.companyName ||
            t("general.carrierFallback"),
        }
      : null
  );

  const [placeOptions, setPlaceOptions] = useState(listCache.places ? listCache.places : []);
  const [industryOptions, setIndustryOptions] = useState(listCache.industries ? listCache.industries : []);
  const [carrierOptions, setCarrierOptions] = useState(listCache.carriers ? listCache.carriers : []);
  const [companyName, setCompanyName] = useState("");

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState(() =>
    Array.isArray(prefill?.clientContactEmails)
      ? prefill.clientContactEmails
          .map((email) => String(email || "").trim().toLowerCase())
          .filter(Boolean)
          .map((email) => ({
            value: email,
            label: email,
          }))
      : []
  );
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState([]);

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
      addEmail(contact?.email);
    });

    addEmail(clientObj.contactEmail);
    addEmail(clientObj.mainContactEmail);
    addEmail(clientObj.secondaryContactEmail);

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  const [pickupPoints, setPickupPoints] = useState(() => {
    const pickups = prefill?.pickupPoints || prefill?.pickupDeliveries || prefill?.pickups || [];
    const mapped = pickups.map((p) => ({
      location: p.location ?? p.placeName ?? "",
      date: p.date ?? "",
      hour: p.hour ?? "",

      // 🆕 point-level dimensions
      length: p.length == null ? "" : String(p.length),
      width: p.width == null ? "" : String(p.width),
      height: p.height == null ? "" : String(p.height),
      lengthError: "",
      widthError: "",
      heightError: "",
      lengthErrorFlag: false,
      widthErrorFlag: false,
      heightErrorFlag: false,

      placeId: p.placeId ?? null,
      address: p.address ?? "",
      postalCode: p.postalCode ?? "",
      city: p.city ?? "",
      country: p.country ?? "",
      gMapsLink: p.gMapsLink ?? "",
      coordinates: p.coordinates ?? "",
      contactPhone: p.contactPhone ?? "",
      // 🆕 NEW
      loadingAccess: p.loadingAccess ?? null,

      // 🆕 Handling equipment
      handlingEquipment: p.handlingEquipment ?? null,
      handlingEquipmentOther: p.handlingEquipmentOther ?? "",

      // 🆕 Reference (optional)
      reference: p.reference ?? "",

      showMoreFields: false,
    }));
    return mapped.length ? mapped : [makeEmptyPoint()];
  });

  const [deliveryPoints, setDeliveryPoints] = useState(() => {
    const deliveries = prefill?.deliveryPoints || prefill?.deliveryDeliveries || prefill?.deliveries || [];
    const mapped = deliveries.map((p) => ({
      location: p.location ?? p.placeName ?? "",
      date: p.date ?? "",
      hour: p.hour ?? "",

      // 🆕 point-level dimensions
      length: p.length == null ? "" : String(p.length),
      width: p.width == null ? "" : String(p.width),
      height: p.height == null ? "" : String(p.height),
      lengthError: "",
      widthError: "",
      heightError: "",
      lengthErrorFlag: false,
      widthErrorFlag: false,
      heightErrorFlag: false,

      placeId: p.placeId ?? null,
      address: p.address ?? "",
      postalCode: p.postalCode ?? "",
      city: p.city ?? "",
      country: p.country ?? "",
      gMapsLink: p.gMapsLink ?? "",
      coordinates: p.coordinates ?? "",
      contactPhone: p.contactPhone ?? "",
      // 🆕 NEW
      loadingAccess: p.loadingAccess ?? null,

      // 🆕 Handling equipment
      handlingEquipment: p.handlingEquipment ?? null,
      handlingEquipmentOther: p.handlingEquipmentOther ?? "",

      // 🆕 Reference (optional)
      reference: p.reference ?? "",

      showMoreFields: false,
    }));
    return mapped.length ? mapped : [makeEmptyPoint()];
  });

  const [transportCarrierId, setTransportCarrierId] = useState(
    prefill?.carrierId != null && String(prefill.carrierId).trim() !== ""
      ? {
          value: String(prefill.carrierId),
          label:
            prefill.carrierName ||
            prefill.loadsAsCarrier?.companyName ||
            t("general.selectedCarrierFallback"),
          isInternal: false,
        }
      : null
  );

  const [availableCarriers, setAvailableCarriers] = useState(listCache.carriers ? listCache.carriers : []);
  const [carrierError, setCarrierError] = useState(false);

  const selectedTransportOptMemo = useMemo(() => {
    return (
      (availableCarriers || []).find(
        (c) => String(c.value) === String(transportCarrierId?.value)
      ) || transportCarrierId || null
    );
  }, [availableCarriers, transportCarrierId]);

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType || !selectedClient?.value) return [];

    const sourceList = clientType === "industry" ? industryOptions : carrierOptions;
    const fullClient = (sourceList || []).find(
      (c) => String(c.value) === String(selectedClient.value)
    );

    return extractClientEmails(fullClient?.raw || null);
  }, [clientType, selectedClient, industryOptions, carrierOptions]);

  const isInternalCarrierMemo = useMemo(() => {
    return (
      selectedTransportOptMemo?.isInternal ||
      String(selectedTransportOptMemo?.value) === "__internal__"
    );
  }, [selectedTransportOptMemo]);

  const showPrice4subcontracted = useMemo(() => {
    if (!transportCarrierId?.value) return false;
    return !isInternalCarrierMemo;
  }, [transportCarrierId, isInternalCarrierMemo]);

  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showPrice4subcontracted || !transportCarrierId?.value) return [];

    const selectedCarrier = (carrierOptions || []).find(
      (c) => String(c.value) === String(transportCarrierId.value)
    );

    return extractClientEmails(selectedCarrier?.raw || null);
  }, [showPrice4subcontracted, transportCarrierId, carrierOptions]);

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";

    if (!Number.isFinite(n) || n <= 0) {
      if (field === "length") return t("validation.lengthRequired");
      if (field === "width") return t("validation.widthRequired");
      if (field === "height") return t("validation.heightRequired");
      return t("validation.fieldRequired", { defaultValue: "❌ Campo obrigatório." });
    }

    const limits = getDimLimits(specialLoad, t);

    if (field === "length" && n > limits.length.max) return limits.length.msg;
    if (field === "width" && n > limits.width.max) return limits.width.msg;
    if (field === "height" && n > limits.height.max) return limits.height.msg;

    return "";
  };

  const handlePointDimensionChange = (index, field, value, isPickup) => {
    const dimensionError = getPointDimensionError(field, value);
    const type = isPickup ? "pickup" : "delivery";

    const apply = (prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
        [`${field}Error`]: dimensionError,
        [`${field}ErrorFlag`]: !!dimensionError,
      };

      return updated;
    };

    if (isPickup) setPickupPoints(apply);
    else setDeliveryPoints(apply);

    setOrClearError(`${type}-${field}-${index}`, !!dimensionError);

    if (formError) setFormError("");
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickupPoints);
  }, [pickupPoints]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPoints);
  }, [deliveryPoints]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches:
        Math.round(pickupLength * 100) === Math.round(deliveryLength * 100),
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength =
      pointDimensionSummary.length > 0
        ? pointDimensionSummary.length.toFixed(2)
        : "";

    const nextWidth =
      pointDimensionSummary.width > 0
        ? pointDimensionSummary.width.toFixed(2)
        : "";

    const nextHeight =
      pointDimensionSummary.height > 0
        ? pointDimensionSummary.height.toFixed(2)
        : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  const emailMultiSelectStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      container: (provided, state) => ({
        ...(base.container ? base.container(provided, state) : provided),
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }),

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 48,
        height: 48,
        overflow: "hidden",
        alignItems: "center",
        boxSizing: "border-box",
        display: "flex",
        flexWrap: "nowrap",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",

        flex: "1 1 auto",
        width: "auto",
        maxWidth: "calc(100% - 86px)",
        minWidth: 0,

        height: 48,
        maxHeight: 48,

        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 12,
        paddingRight: 8,

        gap: 6,
        boxSizing: "border-box",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        flex: "0 0 86px",
        width: 86,
        minWidth: 86,
        maxWidth: 86,
        height: 48,
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 0,
        paddingRight: 6,
        backgroundColor: "#1b1f26",
        zIndex: 5,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        width: "auto",
        maxWidth: 190,
        minWidth: 0,
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 155,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      multiValueRemove: (provided, state) => ({
        ...(base.multiValueRemove ? base.multiValueRemove(provided, state) : provided),
        cursor: "pointer",
        flexShrink: 0,
      }),

      input: (provided, state) => ({
        ...(base.input ? base.input(provided, state) : provided),
        minWidth: 2,
        margin: 0,
        padding: 0,
      }),

      placeholder: (provided, state) => ({
        ...(base.placeholder ? base.placeholder(provided, state) : provided),
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),

      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    };
  }, []);

  const inputRefs = useRef({});

  const closeAllNativePickers = (exceptKey = null) => {
    Object.entries(inputRefs.current).forEach(([key, el]) => {
      if (!el || key === exceptKey) return;

      const type = el.getAttribute?.("type");
      if (type === "date") {
        try {
          el.blur();
        } catch (err) {
          // ignore
        }
      }
    });
  };

  /* ---------- estado de erros por-campo ---------- */
  const [invalidFields, setInvalidFields] = useState({});
  const [clientTypeError, setClientTypeError] = useState(false);
  const [selectedClientError, setSelectedClientError] = useState(false);
  const [formError, setFormError] = useState("");

  /* ---------- Helpers ---------- */
  const isNonEmpty = (v) => (typeof v === "string" ? v.trim() !== "" : !!v);

  const pointIsComplete = (p) => {
    const handlingEquipment = String(
      p.handlingEquipment || ""
    ).trim().toUpperCase();

    return (
      isNonEmpty(p.location) &&
      !p.hourError &&
      !p.lengthErrorFlag &&
      !p.widthErrorFlag &&
      !p.heightErrorFlag &&
      !(
        handlingEquipment === "OTHER" &&
        !isNonEmpty(p.handlingEquipmentOther)
      )
    );
  };

  const isValidHour = (h) => /^([01]\d|2[0-3]):[0-5]\d$/.test(h || "");

  const formatHour = (raw) => {
    const digits = String(raw || "").replace(/[^0-9]/g, "").slice(0, 4);

    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };

  /* ---------- Loading Access ---------- */
  const loadingAccessOptions = [
      { value: "SIDE", label: t("loadingAccess.options.side", { defaultValue: "Side" }) },
      { value: "REAR", label: t("loadingAccess.options.rear", { defaultValue: "Rear" }) },
      { value: "TOP", label: t("loadingAccess.options.top", { defaultValue: "Top" }) },
      { value: "DOCK", label: t("loadingAccess.options.dock", { defaultValue: "Dock" }) },
      { value: "ANY", label: t("loadingAccess.options.any", { defaultValue: "Any" }) },
  ];

  /* ---------- Handling Equipment ---------- */
  const handlingEquipmentOptions = [
    { value: "FORKLIFT", label: t("handlingEquipment.options.FORKLIFT", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK", label: t("handlingEquipment.options.PALLET_JACK", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.options.ELECTRIC_PALLET_JACK", { defaultValue: "Electric pallet jack" }) },
    { value: "CRANE", label: t("handlingEquipment.options.CRANE", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP", label: t("handlingEquipment.options.LOADING_RAMP", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT", label: t("handlingEquipment.options.TAIL_LIFT", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD", label: t("handlingEquipment.options.HAND_LOAD", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR", label: t("handlingEquipment.options.CONVEYOR", { defaultValue: "Conveyor" }) },
    { value: "OTHER", label: t("handlingEquipment.options.OTHER", { defaultValue: "Other" }) },
  ];

  /* ---------- Trailer Type ---------- */
  const trailerTypeOptions = [
    { value: "ANY", label: t("trailerType.options.ANY", { defaultValue: "Any" }) },
    { value: "TAUTLINER", label: t("trailerType.options.TAUTLINER", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER", label: t("trailerType.options.REEFER", { defaultValue: "Reefer" }) }, // ✅ MATCH BACKEND
    { value: "BOX", label: t("trailerType.options.BOX", { defaultValue: "Box" }) },
    { value: "FLATBED", label: t("trailerType.options.FLATBED", { defaultValue: "Flatbed" }) },
    { value: "LOWBED", label: t("trailerType.options.LOWBED", { defaultValue: "Lowbed" }) },
    { value: "TANKER", label: t("trailerType.options.TANKER", { defaultValue: "Tanker" }) },
    { value: "TIPPER", label: t("trailerType.options.TIPPER", { defaultValue: "Tipper" }) },
    { value: "CONTAINER_CHASSIS", label: t("trailerType.options.CONTAINER_CHASSIS", { defaultValue: "Container chassis" }) },
    { value: "MEGA", label: t("trailerType.options.MEGA", { defaultValue: "Mega" }) },
    { value: "OTHER", label: t("trailerType.options.OTHER", { defaultValue: "Other" }) },
  ];

  // Mostra "City, Country" por baixo do nome do place no menu
  const formatPlaceOptionLabel = (option, { context }) => {
    const pd = option.fullData || option;
    const city = pd?.city || "";
    const country = pd?.country || "";
    const subtitle = [city, country].filter(Boolean).join(", ");

    return (
      <div style={{ lineHeight: 1.2 }}>
        <div style={{ fontWeight: 600 }}>{option.label}</div>
        {subtitle ? (
          <div
            style={{
              fontSize: context === "menu" ? 12 : 11,
              opacity: 0.75,
              marginTop: context === "menu" ? 2 : 1,
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
    );
  };

  const clearError = (key) =>
    setInvalidFields((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  const setOrClearError = (key, hasError) =>
    setInvalidFields((prev) => {
      if (hasError) return { ...prev, [key]: true };
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  const handleLoadingAccessChange = (index, opt, isPickup) => {
    const key = `${isPickup ? "pickup" : "delivery"}-loadingAccess-${index}`;
    const value = opt?.value ?? null;

    if (isPickup) {
      setPickupPoints((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], loadingAccess: value };
        return next;
      });
    } else {
      setDeliveryPoints((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], loadingAccess: value };
        return next;
      });
    }

    clearError(key);
  };

  const handleHandlingEquipmentChange = (index, opt, isPickup) => {
    const type = isPickup ? "pickup" : "delivery";
    const value = opt?.value ?? null;

    const keyEquip = `${type}-handlingEquipment-${index}`;
    const keyOther = `${type}-handlingEquipmentOther-${index}`;

    const apply = (prev) => {
      const next = [...prev];
      const prevPoint = next[index] || makeEmptyPoint();

      const normalized = value ? String(value).trim().toUpperCase() : null;

      next[index] = {
        ...prevPoint,
        handlingEquipment: normalized,
        handlingEquipmentOther: normalized === "OTHER" ? (prevPoint.handlingEquipmentOther || "") : "",
      };

      return next;
    };

    if (isPickup) setPickupPoints(apply);
    else setDeliveryPoints(apply);

    // equipamento opcional
    clearError(keyEquip);

    // ✅ Se OTHER: NÃO validar agora. O erro só deve aparecer no submit (markErrorsAndExpand).
    if (String(value || "").trim().toUpperCase() === "OTHER") {
      clearError(keyOther); // garante que não fica vermelho imediatamente
    } else {
      clearError(keyOther);
    }

    if (formError) setFormError("");
  };

  const clearPointErrors = (type, index, keys) => {
    setInvalidFields((prev) => {
      const next = { ...prev };
      (keys || [
        "location",
        "date",
        "hour",
        "length",
        "width",
        "height",
        "address",
        "postalCode",
        "city",
        "country",
        "gMapsLink",
        "coordinates",
        "contactPhone",
        "loadingAccess",
        "handlingEquipment",
        "handlingEquipmentOther",
        "reference",
      ]).forEach((k) => {
        delete next[`${type}-${k}-${index}`];
      });
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCompanyName = async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (cancelled) return;
        setCompanyName(String(res.data?.company_name || "").trim());
      } catch (err) {
        console.warn("❌ Error fetching company name:", err?.response?.status);
        if (!cancelled) setCompanyName("");
      }
    };

    fetchCompanyName();

    return () => {
      cancelled = true;
    };
  }, []);

  // Reindexa invalidFields quando removemos um ponto (para não ficar a apontar para a linha errada)
  const shiftPointErrorsAfterDelete = (type, removedIndex) => {
    setInvalidFields((prev) => {
      const next = {};

      Object.entries(prev || {}).forEach(([key, val]) => {
        // só mexe nas keys do tipo "pickup-...-X" ou "delivery-...-X"
        if (!key.startsWith(`${type}-`)) {
          next[key] = val;
          return;
        }

        // tenta apanhar o índice no fim da key
        const m = key.match(new RegExp(`^${type}-.+-(\\d+)$`));
        if (!m) {
          // se não casar, mantém
          next[key] = val;
          return;
        }

        const idx = Number(m[1]);
        if (!Number.isFinite(idx)) {
          next[key] = val;
          return;
        }

        if (idx === removedIndex) {
          // removemos este ponto -> descartar os erros desta linha
          return;
        }

        if (idx > removedIndex) {
          // tudo o que estava depois "sobe" 1 índice
          const newKey = key.replace(new RegExp(`-(\\d+)$`), `-${idx - 1}`);
          next[newKey] = val;
          return;
        }

        // idx < removedIndex -> fica igual
        next[key] = val;
      });

      return next;
    });
  };

  const mergePoints = (currList, incomingList) => {
    if (!Array.isArray(incomingList) || incomingList.length === 0) return currList;

    const hasRealData =
      Array.isArray(currList) &&
      currList.some((p) => {
        if (!p) return false;
        const fields = [
          p.location,
          p.date,
          p.hour,
          p.length,
          p.width,
          p.height,
          p.address,
          p.postalCode,
          p.city,
          p.country,
          p.gMapsLink,
          p.coordinates,
          p.contactPhone,
          p.loadingAccess,

          // 🆕 Handling equipment
          p.handlingEquipment,
          p.handlingEquipmentOther,

          // 🆕 Reference (optional) -> entra só para "detetar dados", não para validar
          p.reference,
        ];
        return fields.some((v) => (typeof v === "string" ? v.trim() !== "" : !!v));
      });

    if (hasRealData) return currList;

    return incomingList.map((p) => ({
      location: p.location ?? p.placeName ?? "",
      date: p.date ?? "",
      hour: p.hour ?? "",

      // 🆕 point-level dimensions
      length: p.length == null ? "" : String(p.length),
      width: p.width == null ? "" : String(p.width),
      height: p.height == null ? "" : String(p.height),
      lengthError: "",
      widthError: "",
      heightError: "",
      lengthErrorFlag: false,
      widthErrorFlag: false,
      heightErrorFlag: false,

      placeId: p.placeId ?? null,
      address: p.address ?? "",
      postalCode: p.postalCode ?? "",
      city: p.city ?? "",
      country: p.country ?? "",
      gMapsLink: p.gMapsLink ?? "",
      coordinates: p.coordinates ?? "",
      contactPhone: p.contactPhone ?? "",
      // 🆕 NEW
      loadingAccess: p.loadingAccess ?? null,

      // 🆕 Handling equipment
      handlingEquipment: p.handlingEquipment ?? null,
      handlingEquipmentOther: p.handlingEquipmentOther ?? "",

      // 🆕 Reference (optional)
      reference: p.reference ?? "",

      showMoreFields: false,
    }));
  };

  function markErrorsAndExpand(points, type /* 'pickup' | 'delivery' */) {
    const errors = {};

    const updated = points.map((p, i) => {
      if (!isNonEmpty(p.location)) {
        errors[`${type}-location-${i}`] = true;
      }

      ["length", "width", "height"].forEach((field) => {
        const dimensionError = getPointDimensionError(field, p[field]);

        if (dimensionError) {
          errors[`${type}-${field}-${i}`] = dimensionError;
        }
      });

      const handlingEquipment = String(
        p.handlingEquipment || ""
      ).trim().toUpperCase();

      if (
        handlingEquipment === "OTHER" &&
        !isNonEmpty(p.handlingEquipmentOther)
      ) {
        errors[`${type}-handlingEquipmentOther-${i}`] = true;
      }

      return {
        ...p,
        lengthError: errors[`${type}-length-${i}`] || "",
        widthError: errors[`${type}-width-${i}`] || "",
        heightError: errors[`${type}-height-${i}`] || "",
        lengthErrorFlag: !!errors[`${type}-length-${i}`],
        widthErrorFlag: !!errors[`${type}-width-${i}`],
        heightErrorFlag: !!errors[`${type}-height-${i}`],
        showMoreFields:
          p.showMoreFields ||
          !!errors[`${type}-handlingEquipmentOther-${i}`],
      };
    });

    return { errors, updated };
  }

  /* ---------- Sync badge timing ---------- */
  useEffect(() => {
    let timer;
    if (isHydrating) {
      timer = setTimeout(() => setShowSyncBadge(true), 600);
    } else {
      setShowSyncBadge(false);
    }
    return () => clearTimeout(timer);
  }, [isHydrating]);

  useEffect(() => {
    if (
      formError &&
      !lengthError &&
      !heightError &&
      !widthError &&
      !clientTypeError &&
      !selectedClientError &&
      !carrierError &&
      !trailerTypeError &&
      !trailerTypeOtherError &&
      Object.keys(invalidFields).length === 0
    ) {
      setFormError("");
    }
    }, [
      invalidFields,
      lengthError,
      heightError,
      widthError,
      clientTypeError,
      selectedClientError,
      carrierError,
      trailerTypeError,
      trailerTypeOtherError,
      formError,
    ]);

  /* ---------- Preload caches ---------- */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!listCache.places) {
          const res = await axios.get("/places");
          if (!alive) return;
          listCache.places = (res.data || []).map((p) => ({
            label: p.placeName,
            value: p.id,
            fullData: p,
          }));
        }

        if (!listCache.industries || !listCache.carriers) {
          const [industriesRes, carriersRes] = await Promise.all([
            axios.get("/industries"),
            axios.get("/carriers"),
          ]);
          if (!alive) return;

          listCache.industries = (industriesRes.data || []).map((i) => ({
            value: i.id,
            label: i.companyName,
            raw: i,
          }));

          listCache.carriers = (carriersRes.data || []).map((c) => ({
            value: String(c.id),
            label: c.companyName,
            isInternal: false,
            raw: c,
          }));
        }

        if (!alive) return;

        const internalOption =
          companyName && String(companyName).trim()
            ? {
                value: "__internal__",
                label: `${String(companyName).trim()} (internal)`,
                isInternal: true,
              }
            : {
                value: "__internal__",
                label: t("general.internalCarrier", {
                  defaultValue: "Internal carrier",
                }),
                isInternal: true,
              };

        setPlaceOptions(listCache.places || []);
        setIndustryOptions(listCache.industries || []);
        setCarrierOptions(listCache.carriers || []);
        setAvailableCarriers([internalOption, ...(listCache.carriers || [])]);
      } catch (e) {
        console.error("❌ Error preloading options:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [companyName, t]);

  /* ---------- Hydrate do backend ---------- */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setIsHydrating(true);

        const { data: loadData = {} } = await axios.get(`/pendentloads/${loadId}`);
        if (!alive) return;

        const applyHydration = () => {
          setLength((v) => (v === "" || v == null ? loadData.length ?? "" : v));
          setHeight((v) => (v === "" || v == null ? loadData.height ?? "" : v));
          setWidth((v) => (v === "" || v == null ? loadData.width ?? "" : v));
          setWeight((v) => (v === "" || v == null ? loadData.weight ?? "" : v));
          setSpecialLoad((v) => (v ? v : Boolean(loadData.specialLoad)));
          setPrice((v) =>
            v === "" || v == null
              ? (loadData.price != null ? String(loadData.price) : "")
              : v
          );
          setPrice4subcontracted((v) =>
            v === "" || v == null
              ? (loadData.price4subcontracted != null ? String(loadData.price4subcontracted) : "")
              : v
          );

          setSelectedClientContactEmails((prev) => {
            if (Array.isArray(prev) && prev.length > 0) return prev;

            return Array.isArray(loadData.clientContactEmails)
              ? loadData.clientContactEmails
                  .map((email) => String(email || "").trim().toLowerCase())
                  .filter(Boolean)
                  .map((email) => ({
                    value: email,
                    label: email,
                  }))
              : [];
          });

          setSelectedSubcontractedEmails((prev) => {
            if (Array.isArray(prev) && prev.length > 0) return prev;

            return Array.isArray(loadData.subcontractedEmails)
              ? loadData.subcontractedEmails
                  .map((email) => String(email || "").trim().toLowerCase())
                  .filter(Boolean)
                  .map((email) => ({
                    value: email,
                    label: email,
                  }))
              : [];
          });

          const rawTrailerType =
            loadData.trailerType ??
            loadData.trailer_type ??
            loadData.trailerTypeWanted ??
            "";

          const normalizedTrailerType = String(rawTrailerType || "")
            .trim()
            .toUpperCase()
            .replace(/^FRIGO$/, "REEFER");

          setTrailerType((v) => (v === "" || v == null ? normalizedTrailerType : v));

          const rawTrailerTypeOther =
            loadData.trailerTypeOther ??
            loadData.trailer_type_other ??
            "";

          setTrailerTypeOther((v) => (v === "" || v == null ? rawTrailerTypeOther : v));

          setNotes4driver((v) =>
            (v === "" || v == null) && typeof loadData.notes4driver === "string"
              ? loadData.notes4driver
              : v ?? ""
          );

          setMerchandise((v) =>
            (v === "" || v == null) && typeof loadData.merchandise === "string"
              ? loadData.merchandise
              : v ?? ""
          );

          setMerchandiseError("");

          if (!clientType) {
            if (loadData.industryClientId) {
              setClientType("industry");
              setSelectedClient({
                value: loadData.industryClientId,
                label:
                  loadData.industryClient?.companyName ||
                  loadData.industryClientName ||
                  t("general.industryFallback"),
              });
            } else if (loadData.carrierClientId) {
              setClientType("carrier");
              setSelectedClient({
                value: loadData.carrierClientId,
                label:
                  loadData.carrierClient?.companyName ||
                  loadData.carrierClientName ||
                  t("general.carrierFallback"),
              });
            }
          }

          if (loadData?.carrierId != null && String(loadData.carrierId).trim() !== "") {
            setTransportCarrierId({
              value: String(loadData.carrierId),
              label:
                loadData.loadsAsCarrier?.companyName ||
                loadData.carrierName ||
                t("general.selectedCarrierFallback"),
              isInternal: false,
            });
          } else {
            setTransportCarrierId(
              companyName
                ? {
                    value: "__internal__",
                    label: `${companyName} (internal)`,
                    isInternal: true,
                  }
                : {
                    value: "__internal__",
                    label: t("general.internalCarrier", {
                      defaultValue: "Internal carrier",
                    }),
                    isInternal: true,
                  }
            );
          }

          const incomingPickups =
            loadData.pickupPoints || loadData.pickupDeliveries || loadData.pickups || [];
          const incomingDeliveries =
            loadData.deliveryPoints || loadData.deliveryDeliveries || loadData.deliveries || [];

          setPickupPoints((curr) => {
            const merged = mergePoints(curr, incomingPickups);
            return merged.length ? merged : [makeEmptyPoint()];
          });

          setDeliveryPoints((curr) => {
            const merged = mergePoints(curr, incomingDeliveries);
            return merged.length ? merged : [makeEmptyPoint()];
          });
        };

        // ✅ sem flash: se ainda não está ready, aplica já de forma síncrona visual
        if (!ready) {
          applyHydration();
          setReady(true);
        } else {
          startTransition(() => {
            applyHydration();
          });
        }
      } catch (err) {
        console.error("❌ Error hydrating pendent special load:", err);
        if (alive) setReady(true);
      } finally {
        if (alive) setIsHydrating(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [loadId, companyName, t, ready]);

  useEffect(() => {
    if (!availableCarriers?.length) return;

    const internalOpt = availableCarriers.find(
      (c) => c?.isInternal || String(c?.value) === "__internal__"
    );

    if (!internalOpt) return;

    if (!transportCarrierId?.value) {
      setTransportCarrierId(internalOpt);
      return;
    }

    if (String(transportCarrierId.value) === "__internal__") {
      setTransportCarrierId(internalOpt);
    }
  }, [availableCarriers, transportCarrierId]);

useEffect(() => {
  if (!showPrice4subcontracted) {
    setPrice4subcontracted("");
    setPrice4subcontractedError("");
    setSelectedSubcontractedEmails([]);
  }
}, [showPrice4subcontracted]);

  const handleLengthChange = (e) => {
    const val = e.target.value;
    setLength(val);
    setLengthError(validateDim("length", val, specialLoad, t));
    clearError("merch-length");

    // (opcional) limpa aviso special se user já está a corrigir
    if (hasTriedSubmit) {
      setInvalidFields((prev) => {
        const next = { ...prev };
        delete next.specialLoad;
        return next;
      });
    }
  };

  const handleHeightChange = (e) => {
    const val = e.target.value;
    setHeight(val);
    setHeightError(validateDim("height", val, specialLoad, t));
    clearError("merch-height");

    if (hasTriedSubmit) {
      setInvalidFields((prev) => {
        const next = { ...prev };
        delete next.specialLoad;
        return next;
      });
    }
  };

  const handleWidthChange = (e) => {
    const val = e.target.value;
    setWidth(val);
    setWidthError(validateDim("width", val, specialLoad, t));
    clearError("merch-width");

    if (hasTriedSubmit) {
      setInvalidFields((prev) => {
        const next = { ...prev };
        delete next.specialLoad;
        return next;
      });
    }
  };

  const handleWeightChange = (e) => {
    const val = e.target.value;
    setWeight(val);

    if (!String(val ?? "").trim()) {
      setWeightError("");
      clearError("merch-weight");
      return;
    }

    const num = Number(val);

    if (!Number.isFinite(num) || num <= 0) {
      setWeightError(t("validation.weightMustBePositive"));
    } else {
      setWeightError("");
    }

    clearError("merch-weight");
  };

  /* ---------- mantém só o que o backend espera ---------- */
  const sanitizePoints = (points) =>
    points.map((p) => ({
      placeId: p.placeId ?? null,
      location: String(p.location || "").trim(),
      date: String(p.date || "").trim() || null,
      hour: String(p.hour || "").trim() || null,

      // 🆕 point-level dimensions
      length: p.length === "" || p.length == null ? null : Number(p.length),
      width: p.width === "" || p.width == null ? null : Number(p.width),
      height: p.height === "" || p.height == null ? null : Number(p.height),

      address: String(p.address || "").trim() || null,
      postalCode: String(p.postalCode || "").trim() || null,
      city: String(p.city || "").trim() || null,
      country: String(p.country || "").trim() || null,
      gMapsLink: String(p.gMapsLink || "").trim() || null,
      coordinates: String(p.coordinates || "").trim() || null,
      contactPhone: String(p.contactPhone || "").trim() || null,

      loadingAccess:
        String(p.loadingAccess || "").trim().toUpperCase() || null,

      // 🆕 Handling equipment
      handlingEquipment:
        String(p.handlingEquipment || "").trim().toUpperCase() || null,
      handlingEquipmentOther:
        String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
          ? (String(p.handlingEquipmentOther || "").trim() || null)
          : null,

      // 🆕 Reference (optional)
      reference: String(p.reference || "").trim() || null,
    }));

  /* ---------- Save ---------- */
  const handleSaveChanges = async (e) => {
    e.preventDefault();

    setHasTriedSubmit(true);

    setFormError("");
    setInvalidFields({});
    setClientTypeError(false);
    setSelectedClientError(false);
    setCarrierError(false);
    setTrailerTypeError(false);
    setTrailerTypeOtherError(false);

    const currPickups = pickupPoints.length ? pickupPoints : [makeEmptyPoint()];
    const currDeliveries = deliveryPoints.length ? deliveryPoints : [makeEmptyPoint()];

    const hasLength = isNonEmpty(length);
    const hasHeight = isNonEmpty(height);
    const hasWidth = isNonEmpty(width);
    const hasWeight = isNonEmpty(weight);

    const len = hasLength ? Number(length) : null;
    const hei = hasHeight ? Number(height) : null;
    const wid = hasWidth ? Number(width) : null;
    const wei = hasWeight ? Number(weight) : null;

    const lenErrNow = validateDim("length", length, specialLoad, t);
    const heiErrNow = validateDim("height", height, specialLoad, t);
    const widErrNow = validateDim("width", width, specialLoad, t);

    setLengthError(lenErrNow);
    setHeightError(heiErrNow);
    setWidthError(widErrNow);

    const merchErrors = {};

    if (lenErrNow) merchErrors.length = lenErrNow;
    if (heiErrNow) merchErrors.height = heiErrNow;
    if (widErrNow) merchErrors.width = widErrNow;

    let specialNeedsOneErr = "";
    const hasAnyDimension = hasLength || hasHeight || hasWidth;

    if (specialLoad && hasAnyDimension) {
      const hasSpecialDim =
        (Number.isFinite(len) && len >= SPECIAL_THRESHOLD.length) ||
        (Number.isFinite(hei) && hei >= SPECIAL_THRESHOLD.height) ||
        (Number.isFinite(wid) && wid >= SPECIAL_THRESHOLD.width);

      if (!hasSpecialDim) {
        specialNeedsOneErr = t("validation.specialNeedsOneDimension", {
          defaultValue:
            "⚠️ This load is marked as Special Load, but no dimension is special. " +
            `Increase at least one: Length (≥ ${SPECIAL_THRESHOLD.length}m), ` +
            `Height (≥ ${SPECIAL_THRESHOLD.height}m) or Width (≥ ${SPECIAL_THRESHOLD.width}m).`,
        });
      }
    }

    if (hasWeight && (!Number.isFinite(wei) || wei <= 0)) {
      merchErrors.weight = t("validation.weightMustBePositive");
      setWeightError(t("validation.weightMustBePositive"));
    } else {
      setWeightError("");
    }

    const merchandiseValue =
      typeof merchandise === "string" ? merchandise.trim() : "";

    if (!merchandiseValue) {
      merchErrors.merchandise = t("validation.merchandiseRequired");
      setMerchandiseError(t("validation.merchandiseRequired"));
    } else if (merchandiseValue.length > 5000) {
      merchErrors.merchandise = t("errors.merchandiseTooLong", {
        defaultValue: "❌ A mercadoria não pode exceder 5000 caracteres.",
      });
      setMerchandiseError(merchErrors.merchandise);
    } else {
      setMerchandiseError("");
    }

    let hasPriceErr = false;
    let parsedPrice = null;

    {
      const s = String(price ?? "").trim();

      if (s) {
        const n = Number(s);

        if (!Number.isFinite(n) || n < 0) {
          setPriceError(t("validation.invalidPrice"));
          hasPriceErr = true;
        } else {
          parsedPrice = Number(n.toFixed(2));
          setPriceError("");
        }
      } else {
        setPriceError("");
      }
    }

    let hasPrice4subcontractedErr = false;
    let parsedPrice4subcontracted = null;

    {
      const s = String(price4subcontracted ?? "").trim();

      if (showPrice4subcontracted && s) {
        const n = Number(s);

        if (!Number.isFinite(n) || n < 0) {
          setPrice4subcontractedError(t("validation.invalidPrice"));
          hasPrice4subcontractedErr = true;
        } else {
          parsedPrice4subcontracted = Number(n.toFixed(2));
          setPrice4subcontractedError("");
        }
      } else {
        setPrice4subcontractedError("");
      }
    }

    const tt = String(trailerType || "").trim().toUpperCase();
    const needTrailerOther =
      tt === "OTHER" && !isNonEmpty(trailerTypeOther);

    setTrailerTypeError(false);
    setTrailerTypeOtherError(needTrailerOther);

    const { errors: pickErrs, updated: updatedPickups } =
      markErrorsAndExpand(currPickups, "pickup");

    const { errors: delivErrs, updated: updatedDeliveries } =
      markErrorsAndExpand(currDeliveries, "delivery");

    updatedPickups.forEach((point, index) => {
      const invalidHour =
        isNonEmpty(point.hour) &&
        !isValidHour(String(point.hour).trim());

      point.hourError = invalidHour;

      if (invalidHour) {
        pickErrs[`pickup-hour-${index}`] = true;
      }
    });

    updatedDeliveries.forEach((point, index) => {
      const invalidHour =
        isNonEmpty(point.hour) &&
        !isValidHour(String(point.hour).trim());

      point.hourError = invalidHour;

      if (invalidHour) {
        delivErrs[`delivery-hour-${index}`] = true;
      }
    });

    setPickupPoints(updatedPickups);
    setDeliveryPoints(updatedDeliveries);

    const allFieldErrors = {
      ...pickErrs,
      ...delivErrs,
      ...(merchErrors.length ? { "merch-length": merchErrors.length } : {}),
      ...(merchErrors.height ? { "merch-height": merchErrors.height } : {}),
      ...(merchErrors.width ? { "merch-width": merchErrors.width } : {}),
      ...(merchErrors.weight ? { "merch-weight": merchErrors.weight } : {}),
      ...(merchErrors.merchandise
        ? { "merch-merchandise": merchErrors.merchandise }
        : {}),
      ...(specialNeedsOneErr ? { specialLoad: specialNeedsOneErr } : {}),
    };

    setInvalidFields(allFieldErrors);

    const hasRangeErrors = !!(
      lenErrNow ||
      heiErrNow ||
      widErrNow ||
      merchErrors.weight
    );

    const hasSpecialRuleFail = !!specialNeedsOneErr;
    const hasGeneralIssues =
      needTrailerOther ||
      hasPriceErr ||
      hasPrice4subcontractedErr;

    const hasPointIssues =
      Object.keys(pickErrs).length > 0 ||
      Object.keys(delivErrs).length > 0;

    if (
      hasRangeErrors ||
      merchErrors.merchandise ||
      hasSpecialRuleFail ||
      hasGeneralIssues ||
      hasPointIssues
    ) {
      const parts = [];

      if (
        hasRangeErrors ||
        merchErrors.merchandise ||
        hasSpecialRuleFail ||
        hasPriceErr ||
        hasPrice4subcontractedErr
      ) {
        parts.push(t("errors.parts.merchandise"));
      }

      if (needTrailerOther) {
        parts.push(
          t("errors.parts.trailerType", {
            defaultValue: "Tipo de reboque",
          })
        );
      }

      if (hasPointIssues) {
        parts.push(t("errors.parts.points"));
      }

      setFormError(t("errors.fixFollowing", { fields: parts.join(" • ") }));
      return;
    }

    const hasTransportCarrier =
      !!String(transportCarrierId?.value || "").trim();

    const isInternalCarrier =
      hasTransportCarrier &&
      (
        transportCarrierId?.isInternal ||
        String(transportCarrierId?.value) === "__internal__"
      );

    const payload = {
      length: len,
      height: hei,
      width: wid,
      weight: wei,
      merchandise: merchandiseValue,
      price: parsedPrice,
      price4subcontracted: parsedPrice4subcontracted,
      notes4driver:
        notes4driver && notes4driver.trim().length > 0
          ? notes4driver.trim()
          : null,
      specialLoad,

      trailerType: tt || null,
      trailerTypeOther:
        tt === "OTHER"
          ? ((trailerTypeOther ?? "").trim() || null)
          : null,

      industryClientId:
        clientType === "industry" ? selectedClient?.value ?? null : null,
      carrierClientId:
        clientType === "carrier" ? selectedClient?.value ?? null : null,
      clientContactEmails: selectedClientContactEmails
        .map((opt) => String(opt?.value || "").trim().toLowerCase())
        .filter(Boolean),
      subcontractedEmails:
        !hasTransportCarrier || isInternalCarrier
          ? []
          : selectedSubcontractedEmails
              .map((opt) => String(opt?.value || "").trim().toLowerCase())
              .filter(Boolean),
      carrierId:
        !hasTransportCarrier || isInternalCarrier
          ? null
          : Number(transportCarrierId?.value),
      carrierName:
        !hasTransportCarrier
          ? null
          : isInternalCarrier
          ? companyName || null
          : transportCarrierId?.label || null,
      pickupPoints: sanitizePoints(updatedPickups),
      deliveryPoints: sanitizePoints(updatedDeliveries),
    };

    try {
      await axios.put(`/pendentloads/${loadId}`, payload);
      setSuccessPopupOpen(true);
    } catch (err) {
      const backendError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("validation.updateFailed");

      console.error("❌ Error updating special load:", {
        status: err?.response?.status,
        data: err?.response?.data,
        error: err,
      });

      setFormError(backendError);
    }
  };

  if (!ready) return null;


  /* ---------- Render ---------- */
  return (
    <div className="edit-pendent-special-load-container">
      <div
        className="edit-pendent-special-load-back-arrow"
        onClick={() => navigate("/loads?view=pendent")}
      >
        {t("back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-pendent-special-load-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-pendent-special-load-success-title"
        >
          <div className="edit-pendent-special-load-popupModal">
            <div className="edit-pendent-special-load-popupIconWrap">
              <FaCheckCircle className="edit-pendent-special-load-popupIcon" />
            </div>

            <h2
              id="edit-pendent-special-load-success-title"
              className="edit-pendent-special-load-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-pendent-special-load-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-pendent-special-load-popupActions">
              <button
                type="button"
                className="edit-pendent-special-load-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-pendent-special-load-title">{t("title")}</h1>

      <form
        className="edit-pendent-special-load-form"
        onSubmit={handleSaveChanges}
        onFocusCapture={(e) => {
          const target = e.target;
          if (!target) return;

          const currentKey = Object.keys(inputRefs.current).find(
            (key) => inputRefs.current[key] === target
          );

          closeAllNativePickers(currentKey);
        }}
      >
        <div className="edit-pendent-special-load-columns">
          {/* LOADING POINTS */}
          <div className="edit-pendent-special-load-column">
            <h3 className="edit-pendent-special-load-column-title">
              {t("columns.loadingPoints")}
            </h3>

            {(pickupPoints.length ? pickupPoints : [makeEmptyPoint()]).map((point, index) => (
              <div key={`pickup-${index}`} className="edit-pendent-special-load-point-group">
                <div className="edit-pendent-special-load-field-group">
                  <div className="select-wrapper-left-icon">
                    {/* Label + Delete */}
                    <div className="edit-pendent-special-load-label-row">
                      <label
                        className="edit-pendent-special-load-points-label"
                        htmlFor={`pickup-location-${index}`}
                      >
                        {t("labels.pickupPlace")}
                      </label>

                      {pickupPoints.length > 1 && (
                        <button
                          type="button"
                          className="edit-pendent-special-load-delete-btn"
                          onClick={() => {
                            setPickupPoints((prev) => {
                              const next = prev.filter((_, i) => i !== index);
                              return next.length ? next : [makeEmptyPoint()];
                            });

                            // ✅ reindexa os erros (e remove os da linha apagada)
                            shiftPointErrorsAfterDelete("pickup", index);
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <CreatableSelect
                      inputId={`pickup-location-${index}`}
                      isClearable
                      isMulti={false}
                      options={placeOptions}
                      placeholder={t("place.placeholder")}
                      value={
                        point.placeId
                          ? placeOptions.find((p) => p.value === point.placeId) || null
                          : point.location
                          ? {
                              label: point.location,
                              value: point.location,
                              city: point.city,
                              country: point.country,
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        const updated = [...pickupPoints];

                        if (!selectedOption) {
                          updated[index] = { ...makeEmptyPoint(), showMoreFields: false };
                          clearPointErrors("pickup", index);
                          setPickupPoints(updated);
                          return;
                        }

                        if (selectedOption?.__isNew__) {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption.label,
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null,
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            reference: "", // 🆕
                          };
                          clearError(`pickup-location-${index}`);
                        } else if (selectedOption?.fullData) {
                          const p = selectedOption.fullData;
                          updated[index] = {
                            ...updated[index],
                            placeId: p.id,
                            location: p.placeName,
                            address: p.address || "",
                            postalCode: p.postalCode || "",
                            city: p.city || "",
                            country: p.country || "",
                            gMapsLink: p.gMapsLink || "",
                            coordinates: p.coordinates || "",
                            contactPhone: p.contactPhone || "",
                            loadingAccess: p.loadingAccess ?? null,
                          };
                          clearPointErrors("pickup", index);
                        } else {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption?.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                          };
                          if (selectedOption?.label) clearError(`pickup-location-${index}`);
                        }

                        setPickupPoints(updated);
                      }}
                      styles={customSelectStyles}
                      classNamePrefix={invalidFields[`pickup-location-${index}`] ? "select-error" : "select"}
                      formatOptionLabel={formatPlaceOptionLabel}
                    />

                    {invalidFields[`pickup-location-${index}`] && (
                      <p className="edit-pendent-special-load-error">
                        {t("validation.locationRequired")}
                      </p>
                    )}
                  </div>

                  <div className="edit-pendent-special-load-datetime-group">
                    <div className="custom-input-wrapper">
                      <input
                        type="date"
                        value={point.date || ""}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index].date = e.target.value;
                          setPickupPoints(updated);
                          clearError(`pickup-date-${index}`);
                          e.target.blur();
                        }}
                        ref={(el) => (inputRefs.current[`pickup-date-${index}`] = el)}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`pickup-date-${index}`] ? "input-error" : ""
                        }`}
                      />
                      <FaCalendarAlt
                        className="input-icon"
                        onClick={() => {
                          closeAllNativePickers(`pickup-date-${index}`);
                          inputRefs.current[`pickup-date-${index}`]?.focus();
                          inputRefs.current[`pickup-date-${index}`]?.showPicker?.();
                        }}
                      />
                    </div>

                    <div className="custom-input-wrapper">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder={t("placeholders.hour")}
                        value={point.hour || ""}
                        onChange={(e) => {
                          const formatted = formatHour(e.target.value);
                          const updated = [...pickupPoints];
                          updated[index].hour = formatted;
                          setPickupPoints(updated);

                          if (!formatted.trim()) {
                            clearError(`pickup-hour-${index}`);
                          } else if (formatted.length === 5 && !isValidHour(formatted)) {
                            setInvalidFields((prev) => ({
                              ...prev,
                              [`pickup-hour-${index}`]: t("validation.invalidHourFormat"),
                            }));
                          } else {
                            clearError(`pickup-hour-${index}`);
                          }
                        }}
                        ref={(el) => (inputRefs.current[`pickup-hour-${index}`] = el)}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`pickup-hour-${index}`] ? "input-error" : ""
                        }`}
                      />
                      <FaClock
                        className="input-icon"
                        onClick={() => {
                          inputRefs.current[`pickup-hour-${index}`]?.focus();
                        }}
                      />
                    </div>
                  </div>

                  {invalidFields[`pickup-date-${index}`] && (
                    <p className="edit-pendent-special-load-error">{t("validation.dateRequired")}</p>
                  )}

                  {invalidFields[`pickup-hour-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {invalidFields[`pickup-hour-${index}`] === true
                        ? t("validation.hourRequired")
                        : invalidFields[`pickup-hour-${index}`]}
                    </p>
                  )}

                  <div className="edit-pendent-special-load-point-dimensions-block">
                    <label className="edit-pendent-special-load-label edit-pendent-special-load-point-dimensions-title">
                      {t("labels.pointDimensions", {
                        defaultValue: "Dimensões da carga neste ponto (em metros)",
                      })}*
                    </label>

                    <div className="edit-pendent-special-load-point-dimensions-row">
                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointLengthShort", { defaultValue: "Comprimento" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.length || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "length", e.target.value, true)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.lengthErrorFlag || invalidFields[`pickup-length-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.lengthError || invalidFields[`pickup-length-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.lengthError ||
                              invalidFields[`pickup-length-${index}`] ||
                              t("validation.lengthRequired")}
                          </p>
                        )}
                      </div>

                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointWidthShort", { defaultValue: "Largura" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.width || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "width", e.target.value, true)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.widthErrorFlag || invalidFields[`pickup-width-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.widthError || invalidFields[`pickup-width-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.widthError ||
                              invalidFields[`pickup-width-${index}`] ||
                              t("validation.widthRequired")}
                          </p>
                        )}
                      </div>

                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointHeightShort", { defaultValue: "Altura" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.height || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "height", e.target.value, true)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.heightErrorFlag || invalidFields[`pickup-height-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.heightError || invalidFields[`pickup-height-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.heightError ||
                              invalidFields[`pickup-height-${index}`] ||
                              t("validation.heightRequired")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 🆕 Loading Access */}
                  <label className="edit-pendent-special-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-pendent-special-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(point.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={invalidFields[`pickup-loadingAccess-${index}`] ? "select-error" : "select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {invalidFields[`pickup-loadingAccess-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {t("loadingAccess.required", { defaultValue: "Loading access is required." })}
                    </p>
                  )}

                  {/* 🆕 Handling Equipment */}
                  <label className="edit-pendent-special-load-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-pendent-special-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={invalidFields[`pickup-handlingEquipment-${index}`] ? "select-error" : "select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {invalidFields[`pickup-handlingEquipment-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {t("handlingEquipment.required", { defaultValue: "Handling equipment is required." })}
                    </p>
                  )}

                  {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <>
                      <label className="edit-pendent-special-load-label">
                        {t("handlingEquipment.otherLabel", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Specify the equipment..." })}
                        value={point.handlingEquipmentOther || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const updated = [...pickupPoints];
                          updated[index].handlingEquipmentOther = v;
                          setPickupPoints(updated);

                          const key = `pickup-handlingEquipmentOther-${index}`;

                          // ✅ Só limpa o erro se já existia e o user preencheu
                          if (invalidFields[key] && isNonEmpty(v)) clearError(key);

                          if (formError) setFormError("");
                        }}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`pickup-handlingEquipmentOther-${index}`] ? "input-error" : ""
                        }`}
                        style={{ marginBottom: 8 }}
                      />

                      {invalidFields[`pickup-handlingEquipmentOther-${index}`] && (
                        <p className="edit-pendent-special-load-error">
                          {t("validation.handlingEquipmentOtherRequired", {
                            defaultValue: "Please specify the handling equipment.",
                          })}
                        </p>
                      )}
                    </>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <label className="edit-pendent-special-load-label">
                    {t("labels.reference", { defaultValue: "Reference" })}
                  </label>

                  <input
                    type="text"
                    placeholder={t("placeholders.referencePlaceholder", {
                      defaultValue: "e.g.: PO 12345, customer ref...",
                    })}
                    value={point.reference || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const updated = [...pickupPoints];
                      updated[index].reference = v;
                      setPickupPoints(updated);
                      if (formError) setFormError("");
                    }}
                    className="edit-pendent-special-load-input"
                    style={{ marginBottom: 10 }}
                  />

                  {point.showMoreFields ? (
                    <>
                      <div className="edit-pendent-special-load-extra-fields">
                        <input
                          type="text"
                          placeholder={t("placeholders.address")}
                          value={point.address || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].address = e.target.value;
                            setPickupPoints(updated);
                            setOrClearError(`pickup-address-${index}`, !(e.target.value || "").trim());
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`pickup-address-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <div className="edit-pendent-special-load-row-compact">
                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.postalCode")}
                              value={point.postalCode || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].postalCode = e.target.value;
                                setPickupPoints(updated);
                                setOrClearError(
                                  `pickup-postalCode-${index}`,
                                  !(e.target.value || "").trim()
                                );
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`pickup-postalCode-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.city")}
                              value={point.city || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].city = e.target.value;
                                setPickupPoints(updated);
                                setOrClearError(`pickup-city-${index}`, !(e.target.value || "").trim());
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`pickup-city-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.country")}
                              value={point.country || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].country = e.target.value;
                                setPickupPoints(updated);
                                setOrClearError(
                                  `pickup-country-${index}`,
                                  !(e.target.value || "").trim()
                                );
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`pickup-country-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("placeholders.contactPhone")}
                          value={point.contactPhone || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].contactPhone = e.target.value;
                            setPickupPoints(updated);
                            setOrClearError(
                              `pickup-contactPhone-${index}`,
                              !(e.target.value || "").trim()
                            );
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`pickup-contactPhone-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.gMapsLink")}
                          value={point.gMapsLink || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].gMapsLink = e.target.value;
                            setPickupPoints(updated);
                            setOrClearError(`pickup-gMapsLink-${index}`, !(e.target.value || "").trim());
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`pickup-gMapsLink-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.coordinates")}
                          value={point.coordinates || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].coordinates = e.target.value;
                            setPickupPoints(updated);
                            setOrClearError(
                              `pickup-coordinates-${index}`,
                              !(e.target.value || "").trim()
                            );
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`pickup-coordinates-${index}`] ? "input-error" : ""
                          }`}
                        />
                      </div>

                      <div className="edit-pendent-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-pendent-special-load-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...pickupPoints];
                            updated[index].showMoreFields = false;
                            setPickupPoints(updated);
                          }}
                        >
                          {t("buttons.hideFields")}{" "}
                          <FiChevronUp className="edit-pendent-special-load-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-pendent-special-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-pendent-special-load-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...pickupPoints];
                          updated[index].showMoreFields = true;
                          setPickupPoints(updated);
                        }}
                      >
                        {t("buttons.moreFields")}{" "}
                        <FiChevronDown className="edit-pendent-special-load-toggle-arrow" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {pickupPoints.length < MAX_POINTS ? (
              <button
                type="button"
                className="add-location-btn"
                onClick={() =>
                  setPickupPoints((prev) => (prev.length >= MAX_POINTS ? prev : [...prev, makeEmptyPoint()]))
                }
              >
                {t("buttons.addPickup")}
              </button>
            ) : (
              <p className="edit-pendent-special-load-error" style={{ marginTop: 8 }}>
                {t("validation.maxPointsReached", { defaultValue: `⚠️ Maximum ${MAX_POINTS} loading points.` })}
              </p>
            )}
          </div>

          {/* MERCHANDISE & CLIENT */}
          <div className="edit-pendent-special-load-column">
            <h3 className="edit-pendent-special-load-column-title">{t("columns.merchandise")}</h3>

            <div className="edit-pendent-special-load-merchandise-inline-row">
              <div className="edit-pendent-special-load-field-group edit-pendent-special-load-weight-field">
                <label className="edit-pendent-special-load-label">
                  {t("labels.weight")}
                </label>

                <input
                  type="number"
                  value={weight}
                  onChange={handleWeightChange}
                  className={`edit-pendent-special-load-input ${
                    invalidFields["merch-weight"] || weightError ? "input-error" : ""
                  }`}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />

                {(invalidFields["merch-weight"] || weightError) && (
                  <p className="edit-pendent-special-load-error">
                    {weightError || invalidFields["merch-weight"]}
                  </p>
                )}
              </div>

              <div className="edit-pendent-special-load-field-group edit-pendent-special-load-merchandise-field">
                <label className="edit-pendent-special-load-label">
                  {t("labels.merchandise")}
                </label>

                <input
                  type="text"
                  placeholder={t("placeholders.merchandise")}
                  value={merchandise}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMerchandise(v);

                    if (merchandiseError && (v || "").trim()) setMerchandiseError("");
                    if (formError) setFormError("");
                  }}
                  className={`edit-pendent-special-load-input ${
                    merchandiseError ? "input-error" : ""
                  }`}
                />

                {merchandiseError && (
                  <p className="edit-pendent-special-load-error">
                    {merchandiseError}
                  </p>
                )}
              </div>
            </div>

            <div className="edit-pendent-special-load-load-dimensions-summary">
              <label className="edit-pendent-special-load-label edit-pendent-special-load-load-dimensions-title">
                {t("labels.loadDimensions", {
                  defaultValue: "Dimensões totais da carga",
                })}
              </label>

              <div className="edit-pendent-special-load-load-dimensions-row">
                <div className="edit-pendent-special-load-load-dimension-field">
                  <label>
                    {t("labels.length", { defaultValue: "Comprimento (m)" })}
                  </label>
                  <input
                    type="text"
                    value={length}
                    readOnly
                    className={`edit-pendent-special-load-input edit-pendent-special-load-auto-calculated-input ${
                      invalidFields["merch-length"] || lengthError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div className="edit-pendent-special-load-load-dimension-field">
                  <label>
                    {t("labels.width", { defaultValue: "Largura (m)" })}
                  </label>
                  <input
                    type="text"
                    value={width}
                    readOnly
                    className={`edit-pendent-special-load-input edit-pendent-special-load-auto-calculated-input ${
                      invalidFields["merch-width"] || widthError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div className="edit-pendent-special-load-load-dimension-field">
                  <label>
                    {t("labels.height", { defaultValue: "Altura (m)" })}
                  </label>
                  <input
                    type="text"
                    value={height}
                    readOnly
                    className={`edit-pendent-special-load-input edit-pendent-special-load-auto-calculated-input ${
                      invalidFields["merch-height"] || heightError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {(invalidFields["merch-length"] || lengthError) && (
                <p className="edit-pendent-special-load-error">
                  {lengthError || invalidFields["merch-length"]}
                </p>
              )}

              {(invalidFields["merch-width"] || widthError) && (
                <p className="edit-pendent-special-load-error">
                  {widthError || invalidFields["merch-width"]}
                </p>
              )}

              {(invalidFields["merch-height"] || heightError) && (
                <p className="edit-pendent-special-load-error">
                  {heightError || invalidFields["merch-height"]}
                </p>
              )}

              {!pointDimensionSummary.lengthMatches && (
                <p className="edit-pendent-special-load-dimension-warning-text">
                  {t("validation.pickupDeliveryLengthMismatch", {
                    defaultValue:
                      "⚠️ A soma dos comprimentos das cargas e descargas ainda não coincide.",
                  })}
                </p>
              )}

              {hasTriedSubmit && invalidFields?.specialLoad && (
                <p className="edit-pendent-special-load-warning-text">
                  {invalidFields.specialLoad}
                </p>
              )}

              <div className="edit-pendent-special-load-dimension-helper-text">
                {t("labels.loadDimensionsHelper", {
                  defaultValue:
                    "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                })}
              </div>
            </div>

            {/* 🆕 Trailer Type */}
            <div className="edit-pendent-special-load-field-group">
              <label className="edit-pendent-special-load-label">
                {t("trailerType.label", { defaultValue: "Trailer type*" })}
              </label>

              <Select
                options={trailerTypeOptions}
                value={
                  // mostra o valor mesmo que não exista nas opções (não fica em branco)
                  trailerTypeOptions.find(
                    (o) => o.value === String(trailerType || "").trim().toUpperCase()
                  ) ||
                  (String(trailerType || "").trim()
                    ? {
                        value: String(trailerType || "").trim().toUpperCase(),
                        label: String(trailerType || "").trim(),
                      }
                    : null)
                }
                onChange={(opt) => {
                  const v = opt?.value || "";
                  setTrailerType(v);
                  setTrailerTypeError(false);

                  if (String(v || "").toUpperCase() !== "OTHER") {
                    setTrailerTypeOther("");
                    setTrailerTypeOtherError(false);
                  } else {
                    // ✅ não mostrar erro só por escolher OTHER
                    setTrailerTypeOtherError(hasTriedSubmit && !isNonEmpty(trailerTypeOther));
                  }

                  if (formError) setFormError("");
                }}
                isClearable
                placeholder={t("trailerType.placeholder", { defaultValue: "Select trailer type..." })}
                styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                classNamePrefix={trailerTypeError ? "select-error" : "select"}
                menuPortalTarget={document.body}
              />

              {trailerTypeError && (
                <p className="edit-pendent-special-load-error">
                  {t("validation.trailerTypeRequired", { defaultValue: "❌ Trailer type is required." })}
                </p>
              )}
            </div>

            {String(trailerType || "").trim().toUpperCase() === "OTHER" && (
              <div className="edit-pendent-special-load-field-group">
                <label className="edit-pendent-special-load-label">
                  {t("trailerType.otherLabel", { defaultValue: "Other (specify)*" })}
                </label>

                <input
                  type="text"
                  placeholder={t("trailerType.otherPlaceholder", { defaultValue: "Specify trailer type..." })}
                  value={trailerTypeOther || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTrailerTypeOther(v);

                    // ✅ só validar depois de submit
                    setTrailerTypeOtherError(hasTriedSubmit && !isNonEmpty(v));

                    if (formError) setFormError("");
                  }}
                  className={`edit-pendent-special-load-input ${trailerTypeOtherError ? "input-error" : ""}`}
                />

                {trailerTypeOtherError && (
                  <p className="edit-pendent-special-load-error">
                    {t("validation.trailerTypeOtherRequired", {
                      defaultValue: "❌ Please specify the trailer type.",
                    })}
                  </p>
                )}
              </div>
            )}

            <h3 className="edit-pendent-special-load-column-title">{t("columns.generalInfo")}</h3>

            <div className="edit-pendent-special-load-field-group">
              <label className="edit-pendent-special-load-label-price">{t("labels.price")}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  setPriceError("");
                  if (formError) setFormError("");
                }}
                onBlur={() => {
                  const s = String(price ?? "").trim();
                  if (!s) {
                    setPriceError("");
                    return;
                  }
                  const n = Number(s);
                  if (!Number.isFinite(n) || n < 0) setPriceError(t("validation.invalidPrice"));
                  else setPriceError("");
                }}
                className={`edit-pendent-special-load-input ${priceError ? "input-error" : ""}`}
                placeholder={t("placeholders.price")}
              />
              {priceError && <p className="edit-pendent-special-load-error">{priceError}</p>}
            </div>

            {showPrice4subcontracted && (
              <div className="edit-pendent-special-load-field-group">
                <label className="edit-pendent-special-load-label-price">
                  {t("labels.price4subcontracted", {
                    defaultValue: "Price for Subcontracted",
                  })}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price4subcontracted}
                  onChange={(e) => {
                    setPrice4subcontracted(e.target.value);
                    setPrice4subcontractedError("");
                    if (formError) setFormError("");
                  }}
                  onBlur={() => {
                    const s = String(price4subcontracted ?? "").trim();
                    if (!s) {
                      setPrice4subcontractedError("");
                      return;
                    }
                    const n = Number(s);
                    if (!Number.isFinite(n) || n < 0) {
                      setPrice4subcontractedError(t("validation.invalidPrice"));
                    } else {
                      setPrice4subcontractedError("");
                    }
                  }}
                  className={`edit-pendent-special-load-input ${
                    price4subcontractedError ? "input-error" : ""
                  }`}
                  placeholder={t("placeholders.price4subcontracted")}
                />
                {price4subcontractedError && (
                  <p className="edit-pendent-special-load-error">
                    {price4subcontractedError}
                  </p>
                )}
              </div>
            )}

            <div className="edit-pendent-special-load-field-group">
              <label className="edit-pendent-special-load-label">{t("labels.clientType")}</label>
              <Select
                value={
                  clientType
                    ? {
                        value: clientType,
                        label: clientType === "industry" ? t("labels.industry") : t("labels.carrier"),
                      }
                    : null
                }
                onChange={(selected) => {
                  setClientType(selected?.value || "");
                  setSelectedClient(null);
                  setSelectedClientContactEmails([]);
                  setSelectedSubcontractedEmails([]);
                  setClientTypeError(false);

                  const internalOpt = availableCarriers.find(
                    (c) => c?.isInternal || String(c?.value) === "__internal__"
                  );

                  if (internalOpt) {
                    setTransportCarrierId(internalOpt);
                    setCarrierError(false);
                  }

                  setPrice4subcontractedError("");
                }}
                options={[
                  { value: "industry", label: t("labels.industry") },
                  { value: "carrier", label: t("labels.carrier") },
                ]}
                styles={customSelectStyles}
                isClearable
                isSearchable={false}
                classNamePrefix={clientTypeError ? "select-error" : "select"}
              />
              {clientTypeError && (
                <p className="edit-pendent-special-load-error">{t("validation.clientTypeRequired")}</p>
              )}
            </div>

            {clientType && (
              <div className="edit-pendent-special-load-field-group">
                <label className="edit-pendent-special-load-label">
                  {clientType === "industry" ? t("labels.industryClient") : t("labels.carrierClient")}
                </label>
                <Select
                  value={selectedClient}
                  onChange={(v) => {
                    setSelectedClient(v);
                    setSelectedClientContactEmails([]);
                    setSelectedClientError(false);
                  }}
                  options={clientType === "industry" ? industryOptions : carrierOptions}
                  styles={customSelectStyles}
                  isClearable
                  classNamePrefix={selectedClientError ? "select-error" : "select"}
                  placeholder={t("placeholders.clientSearch")}
                />
                {selectedClientError && (
                  <p className="edit-pendent-special-load-error">{t("validation.clientRequired")}</p>
                )}

                {selectedClient?.value && (
                  <div className="edit-pendent-special-load-field-group">
                    <label className="edit-pendent-special-load-label">
                      {t("clientContactEmails.label", {
                        defaultValue: "Client contact email(s)",
                      })}
                    </label>

                    <Select
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      options={selectedClientContactEmailOptions}
                      value={selectedClientContactEmails}
                      onChange={(selectedOptions) => {
                        const limited = Array.isArray(selectedOptions)
                          ? selectedOptions.slice(0, 3)
                          : [];
                        setSelectedClientContactEmails(limited);
                      }}
                      placeholder={t("clientContactEmails.placeholder", {
                        defaultValue: "Select up to 3 contact email(s)...",
                      })}
                      noOptionsMessage={() =>
                        t("clientContactEmails.noOptions", {
                          defaultValue: "No contact emails found for this client.",
                        })
                      }
                      styles={emailMultiSelectStyles}
                      classNamePrefix="email-multi-select"
                      isSearchable={false}
                      menuPortalTarget={document.body}
                    />

                    <div
                      className="edit-pendent-special-load-help-text"
                      style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}
                    >
                      {t("clientContactEmails.helper", {
                        defaultValue: "These emails come from the client's contact list.",
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="edit-pendent-special-load-field-group">
              <label className="edit-pendent-special-load-label">{t("labels.transportCarrier")}</label>
              <Select
                value={
                  availableCarriers.find(
                    (c) => String(c.value) === String(transportCarrierId?.value)
                  ) ||
                  transportCarrierId ||
                  null
                }
                onChange={(selected) => {
                  setSelectedSubcontractedEmails([]);

                  if (!selected) {
                    const internalOpt = availableCarriers.find(
                      (c) => c?.isInternal || String(c?.value) === "__internal__"
                    );
                    setTransportCarrierId(internalOpt || null);
                  } else {
                    setTransportCarrierId(selected);
                  }
                  setCarrierError(false);
                  setPrice4subcontractedError("");
                }}
                options={availableCarriers}
                styles={customSelectStyles}
                isClearable
                classNamePrefix={carrierError ? "select-error" : "select"}
                placeholder={t("placeholders.carrierSelect")}
              />
              {carrierError && (
                <p className="edit-pendent-special-load-error">{t("validation.transportCarrierRequired")}</p>
              )}
            </div>

            {showPrice4subcontracted && (
              <div className="edit-pendent-special-load-field-group">
                <label className="edit-pendent-special-load-label">
                  {t("subcontractedEmails.label", {
                    defaultValue: "Subcontracted email(s)",
                  })}
                </label>

                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={selectedSubcontractedEmailOptions}
                  value={selectedSubcontractedEmails}
                  onChange={(selectedOptions) => {
                    const limited = Array.isArray(selectedOptions)
                      ? selectedOptions.slice(0, 3)
                      : [];
                    setSelectedSubcontractedEmails(limited);
                  }}
                  placeholder={t("subcontractedEmails.placeholder", {
                    defaultValue: "Select up to 3 subcontracted email(s)...",
                  })}
                  noOptionsMessage={() =>
                    t("subcontractedEmails.noOptions", {
                      defaultValue: "No contact emails found for this carrier.",
                    })
                  }
                  styles={emailMultiSelectStyles}
                  classNamePrefix="email-multi-select"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                />

                <div
                  className="edit-pendent-special-load-help-text"
                  style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}
                >
                  {t("subcontractedEmails.helper", {
                    defaultValue:
                      "These emails come from the selected transport carrier contact list.",
                  })}
                </div>
              </div>
            )}
          </div>

          {/* UNLOADING POINTS */}
          <div className="edit-pendent-special-load-column">
            <h3 className="edit-pendent-special-load-column-title">
              {t("columns.unloadingPoints")}
            </h3>

            {(deliveryPoints.length ? deliveryPoints : [makeEmptyPoint()]).map((point, index) => (
              <div key={`delivery-${index}`} className="edit-pendent-special-load-point-group">
                <div className="edit-pendent-special-load-field-group">
                  <div className="select-wrapper-left-icon">
                    {/* Label + Delete */}
                    <div className="edit-pendent-special-load-label-row">
                      <label
                        className="edit-pendent-special-load-points-label"
                        htmlFor={`delivery-location-${index}`}
                      >
                        {t("labels.deliveryPlace")}
                      </label>

                      {deliveryPoints.length > 1 && (
                        <button
                          type="button"
                          className="edit-pendent-special-load-delete-btn"
                          onClick={() => {
                            setDeliveryPoints((prev) => {
                              const next = prev.filter((_, i) => i !== index);
                              return next.length ? next : [makeEmptyPoint()];
                            });

                            // ✅ reindexa os erros (e remove os da linha apagada)
                            shiftPointErrorsAfterDelete("delivery", index);
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <CreatableSelect
                      inputId={`delivery-location-${index}`}
                      isClearable
                      isMulti={false}
                      options={placeOptions}
                      placeholder={t("place.placeholder")}
                      value={
                        point.placeId
                          ? placeOptions.find((p) => p.value === point.placeId) || null
                          : point.location
                          ? {
                              label: point.location,
                              value: point.location,
                              city: point.city,
                              country: point.country,
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        const updated = [...deliveryPoints];

                        if (!selectedOption) {
                          updated[index] = { ...makeEmptyPoint(), showMoreFields: false };
                          clearPointErrors("delivery", index);
                          setDeliveryPoints(updated);
                          return;
                        }

                        if (selectedOption?.__isNew__) {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption.label,
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null,
                            handlingEquipment: null,
                            handlingEquipmentOther: "",
                            reference: "", // 🆕
                          };
                          clearError(`delivery-location-${index}`);
                        } else if (selectedOption?.fullData) {
                          const p = selectedOption.fullData;
                          updated[index] = {
                            ...updated[index],
                            placeId: p.id,
                            location: p.placeName,
                            address: p.address || "",
                            postalCode: p.postalCode || "",
                            city: p.city || "",
                            country: p.country || "",
                            gMapsLink: p.gMapsLink || "",
                            coordinates: p.coordinates || "",
                            contactPhone: p.contactPhone || "",
                            loadingAccess: p.loadingAccess ?? null,
                          };
                          clearPointErrors("delivery", index);
                        } else {
                          updated[index] = {
                            ...updated[index],
                            placeId: null,
                            location: selectedOption?.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                          };
                          if (selectedOption?.label) clearError(`delivery-location-${index}`);
                        }

                        setDeliveryPoints(updated);
                      }}
                      styles={customSelectStyles}
                      classNamePrefix={invalidFields[`delivery-location-${index}`] ? "select-error" : "select"}
                      formatOptionLabel={formatPlaceOptionLabel}
                    />

                    {invalidFields[`delivery-location-${index}`] && (
                      <p className="edit-pendent-special-load-error">
                        {t("validation.locationRequired")}
                      </p>
                    )}
                  </div>

                  <div className="edit-pendent-special-load-datetime-group">
                    <div className="custom-input-wrapper">
                      <input
                        type="date"
                        value={point.date || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index].date = e.target.value;
                          setDeliveryPoints(updated);
                          clearError(`delivery-date-${index}`);
                          e.target.blur();
                        }}
                        ref={(el) => (inputRefs.current[`delivery-date-${index}`] = el)}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`delivery-date-${index}`] ? "input-error" : ""
                        }`}
                      />
                      <FaCalendarAlt
                        className="input-icon"
                        onClick={() => {
                          closeAllNativePickers(`delivery-date-${index}`);
                          inputRefs.current[`delivery-date-${index}`]?.focus();
                          inputRefs.current[`delivery-date-${index}`]?.showPicker?.();
                        }}
                      />
                    </div>

                    <div className="custom-input-wrapper">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder={t("placeholders.hour")}
                        value={point.hour || ""}
                        onChange={(e) => {
                          const formatted = formatHour(e.target.value);
                          const updated = [...deliveryPoints];
                          updated[index].hour = formatted;
                          setDeliveryPoints(updated);

                          if (!formatted.trim()) {
                            clearError(`delivery-hour-${index}`);
                          } else if (formatted.length === 5 && !isValidHour(formatted)) {
                            setInvalidFields((prev) => ({
                              ...prev,
                              [`delivery-hour-${index}`]: t("validation.invalidHourFormat"),
                            }));
                          } else {
                            clearError(`delivery-hour-${index}`);
                          }
                        }}
                        ref={(el) => (inputRefs.current[`delivery-hour-${index}`] = el)}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`delivery-hour-${index}`] ? "input-error" : ""
                        }`}
                      />
                      <FaClock
                        className="input-icon"
                        onClick={() => {
                          inputRefs.current[`delivery-hour-${index}`]?.focus();
                        }}
                      />
                    </div>
                  </div>

                  {invalidFields[`delivery-date-${index}`] && (
                    <p className="edit-pendent-special-load-error">{t("validation.dateRequired")}</p>
                  )}

                  {invalidFields[`delivery-hour-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {invalidFields[`delivery-hour-${index}`] === true
                        ? t("validation.hourRequired")
                        : invalidFields[`delivery-hour-${index}`]}
                    </p>
                  )}

                  <div className="edit-pendent-special-load-point-dimensions-block">
                    <label className="edit-pendent-special-load-label edit-pendent-special-load-point-dimensions-title">
                      {t("labels.pointDimensions", {
                        defaultValue: "Dimensões da carga neste ponto (em metros)",
                      })}*
                    </label>

                    <div className="edit-pendent-special-load-point-dimensions-row">
                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointLengthShort", { defaultValue: "Comprimento" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.length || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "length", e.target.value, false)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.lengthErrorFlag || invalidFields[`delivery-length-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.lengthError || invalidFields[`delivery-length-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.lengthError ||
                              invalidFields[`delivery-length-${index}`] ||
                              t("validation.lengthRequired")}
                          </p>
                        )}
                      </div>

                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointWidthShort", { defaultValue: "Largura" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.width || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "width", e.target.value, false)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.widthErrorFlag || invalidFields[`delivery-width-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.widthError || invalidFields[`delivery-width-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.widthError ||
                              invalidFields[`delivery-width-${index}`] ||
                              t("validation.widthRequired")}
                          </p>
                        )}
                      </div>

                      <div className="edit-pendent-special-load-point-dimension-field">
                        <label>
                          {t("labels.pointHeightShort", { defaultValue: "Altura" })}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={point.height || ""}
                          onChange={(e) =>
                            handlePointDimensionChange(index, "height", e.target.value, false)
                          }
                          className={`edit-pendent-special-load-input ${
                            point.heightErrorFlag || invalidFields[`delivery-height-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                          placeholder="0.00m"
                        />
                        {(point.heightError || invalidFields[`delivery-height-${index}`]) && (
                          <p className="edit-pendent-special-load-error">
                            {point.heightError ||
                              invalidFields[`delivery-height-${index}`] ||
                              t("validation.heightRequired")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 🆕 Loading Access */}
                  <label className="edit-pendent-special-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-pendent-special-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(point.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={invalidFields[`delivery-loadingAccess-${index}`] ? "select-error" : "select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {invalidFields[`delivery-loadingAccess-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {t("loadingAccess.required", { defaultValue: "Loading access is required." })}
                    </p>
                  )}

                  {/* 🆕 Handling Equipment */}
                  <label className="edit-pendent-special-load-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-pendent-special-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={invalidFields[`delivery-handlingEquipment-${index}`] ? "select-error" : "select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {invalidFields[`delivery-handlingEquipment-${index}`] && (
                    <p className="edit-pendent-special-load-error">
                      {t("handlingEquipment.required", { defaultValue: "Handling equipment is required." })}
                    </p>
                  )}

                  {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <>
                      <label className="edit-pendent-special-load-label">
                        {t("handlingEquipment.otherLabel", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Specify the equipment..." })}
                        value={point.handlingEquipmentOther || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const updated = [...deliveryPoints];
                          updated[index].handlingEquipmentOther = v;
                          setDeliveryPoints(updated);

                          const key = `delivery-handlingEquipmentOther-${index}`;

                          // ✅ Só limpa o erro se já existia e o user preencheu
                          if (invalidFields[key] && isNonEmpty(v)) clearError(key);

                          if (formError) setFormError("");
                        }}
                        className={`edit-pendent-special-load-input ${
                          invalidFields[`delivery-handlingEquipmentOther-${index}`] ? "input-error" : ""
                        }`}
                        style={{ marginBottom: 8 }}
                      />

                      {invalidFields[`delivery-handlingEquipmentOther-${index}`] && (
                        <p className="edit-pendent-special-load-error">
                          {t("validation.handlingEquipmentOtherRequired", {
                            defaultValue: "Please specify the handling equipment.",
                          })}
                        </p>
                      )}
                    </>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <label className="edit-pendent-special-load-label">
                    {t("reference", { defaultValue: "Reference" })}
                  </label>

                  <input
                    type="text"
                    placeholder={t("referencePlaceholder", { defaultValue: "e.g.: PO 12345, customer ref..." })}
                    value={point.reference || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const updated = [...deliveryPoints];
                      updated[index].reference = v;
                      setDeliveryPoints(updated);
                      if (formError) setFormError("");
                    }}
                    className="edit-pendent-special-load-input"
                    style={{ marginBottom: 10 }}
                  />

                  {point.showMoreFields ? (
                    <>
                      <div className="edit-pendent-special-load-extra-fields">
                        <input
                          type="text"
                          placeholder={t("placeholders.address")}
                          value={point.address || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].address = e.target.value;
                            setDeliveryPoints(updated);
                            setOrClearError(`delivery-address-${index}`, !(e.target.value || "").trim());
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`delivery-address-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <div className="edit-pendent-special-load-row-compact">
                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.postalCode")}
                              value={point.postalCode || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].postalCode = e.target.value;
                                setDeliveryPoints(updated);
                                setOrClearError(
                                  `delivery-postalCode-${index}`,
                                  !(e.target.value || "").trim()
                                );
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`delivery-postalCode-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.city")}
                              value={point.city || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].city = e.target.value;
                                setDeliveryPoints(updated);
                                setOrClearError(`delivery-city-${index}`, !(e.target.value || "").trim());
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`delivery-city-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <div className="edit-pendent-special-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeholders.country")}
                              value={point.country || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].country = e.target.value;
                                setDeliveryPoints(updated);
                                setOrClearError(
                                  `delivery-country-${index}`,
                                  !(e.target.value || "").trim()
                                );
                              }}
                              className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                                invalidFields[`delivery-country-${index}`] ? "input-error" : ""
                              }`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("placeholders.contactPhone")}
                          value={point.contactPhone || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].contactPhone = e.target.value;
                            setDeliveryPoints(updated);
                            setOrClearError(
                              `delivery-contactPhone-${index}`,
                              !(e.target.value || "").trim()
                            );
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`delivery-contactPhone-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.gMapsLink")}
                          value={point.gMapsLink || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].gMapsLink = e.target.value;
                            setDeliveryPoints(updated);
                            setOrClearError(`delivery-gMapsLink-${index}`, !(e.target.value || "").trim());
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`delivery-gMapsLink-${index}`] ? "input-error" : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("placeholders.coordinates")}
                          value={point.coordinates || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].coordinates = e.target.value;
                            setDeliveryPoints(updated);
                            setOrClearError(
                              `delivery-coordinates-${index}`,
                              !(e.target.value || "").trim()
                            );
                          }}
                          className={`edit-pendent-special-load-input edit-pendent-special-load-main-field ${
                            invalidFields[`delivery-coordinates-${index}`] ? "input-error" : ""
                          }`}
                        />
                      </div>

                      <div className="edit-pendent-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-pendent-special-load-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...deliveryPoints];
                            updated[index].showMoreFields = false;
                            setDeliveryPoints(updated);
                          }}
                        >
                          {t("buttons.hideFields")}{" "}
                          <FiChevronUp className="edit-pendent-special-load-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-pendent-special-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-pendent-special-load-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPoints];
                          updated[index].showMoreFields = true;
                          setDeliveryPoints(updated);
                        }}
                      >
                        {t("buttons.moreFields")}{" "}
                        <FiChevronDown className="edit-pendent-special-load-toggle-arrow" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {deliveryPoints.length < MAX_POINTS ? (
              <button
                type="button"
                className="add-location-btn"
                onClick={() =>
                  setDeliveryPoints((prev) => (prev.length >= MAX_POINTS ? prev : [...prev, makeEmptyPoint()]))
                }
              >
                {t("buttons.addDelivery")}
              </button>
            ) : (
              <p className="edit-pendent-special-load-error" style={{ marginTop: 8 }}>
                {t("validation.maxPointsReached", { defaultValue: `⚠️ Maximum ${MAX_POINTS} unloading points.` })}
              </p>
            )}
          </div>
        </div>

        <div className="edit-pendent-special-load-field-group">
          <label className="edit-pendent-special-load-label-notes">{t("labels.notes4driver")}</label>
          <textarea
            rows={4}
            value={notes4driver}
            onChange={(e) => {
              setNotes4driver(e.target.value);
              if (formError) setFormError("");
            }}
            placeholder={t("placeholders.notes4driver")}
            className="edit-pendent-special-load-input"
            style={{ resize: "vertical" }}
          />
        </div>

        <div className="edit-pendent-special-load-button-group">
          <button
            type="button"
            onClick={() => navigate("/loads?view=pendent")}
            className="edit-pendent-special-load-cancel-button"
          >
            {t("buttons.cancel")}
          </button>
          <button
            type="submit"
            className="edit-pendent-special-load-save-button"
            disabled={
              !!lengthError ||
              !!heightError ||
              !!widthError ||
              !!weightError ||
              !!priceError ||
              !!price4subcontractedError ||
              !!merchandiseError
            }
          >
            {t("buttons.saveChanges")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditPendentSpecialLoad;
